import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { noticeAPI, membershipAPI } from '../../actions/imsServer';
import ContentArea from '../../components/contentArea';
import { formatDateTimeDisplay } from '../../utils/base';
import { useTheme } from '@mui/material/styles';
import { API_PAGE_SIZE } from '../../constants';

const ITEMS_PER_PAGE = API_PAGE_SIZE;

export const NoticeList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();

    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const offsetRef = useRef(0);
    const userOrganizationIdsRef = useRef(null);

    const filterNotices = useCallback((rawNotices, grade) => {
        const today = new Date();
        return (rawNotices || []).filter(notice => {
            const noticeDate = new Date(notice.startDate);
            const isStateTwoValid =
                notice.state !== 2 || noticeDate <= today;

            let isTypeValid;
            if (grade < 50) {
                isTypeValid = [0, 1, 3].includes(notice.type);
            } else {
                isTypeValid = [0, 1, 2].includes(notice.type);
            }

            return isStateTwoValid && isTypeValid;
        });
    }, []);

    const fetchNotices = useCallback(async (reset = false) => {
        try {
            const meInfo = JSON.parse(localStorage.getItem('meInfo'));
            if (!meInfo?.id) {
                setNotices([]);
                setLoading(false);
                return;
            }

            const grade = Number(meInfo?.grade || 0);
            const currentOffset = reset ? 0 : offsetRef.current;

            if (reset) {
                setLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            let userOrganizationIds = userOrganizationIdsRef.current;
            if (userOrganizationIds === null) {
                try {
                    const membershipsResponse = await membershipAPI.getMemberships({
                        userId: meInfo.id,
                        type: 1,
                        state: 0,
                    });
                    const memberships = membershipsResponse?.data?.memberships ||
                        membershipsResponse?.memberships ||
                        (Array.isArray(membershipsResponse) ? membershipsResponse : []);
                    userOrganizationIds = memberships.map(m => m.organizationId).filter(id => id != null);
                    userOrganizationIdsRef.current = userOrganizationIds;
                } catch (err) {
                    console.error('Failed to fetch memberships:', err);
                    userOrganizationIds = [];
                }
            }

            const res = await noticeAPI.getNotices({
                organizationId: [0, ...userOrganizationIds].join(','),
                state: '0,2',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
            });

            const rawNotices = Array.isArray(res) ? res : (res?.data?.notices || []);
            const total = res?.data?.total ?? res?.total ?? 0;
            const filtered = filterNotices(rawNotices, grade);

            if (reset) {
                setNotices(filtered);
                offsetRef.current = currentOffset + rawNotices.length;
            } else {
                setNotices(prev => [...prev, ...filtered]);
                offsetRef.current = currentOffset + rawNotices.length;
            }

            setHasMore(
                rawNotices.length === ITEMS_PER_PAGE &&
                (total === 0 || offsetRef.current < total)
            );
        } catch (e) {
            console.error('Failed to fetch notices:', e);
            if (reset) setNotices([]);
            setHasMore(false);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
        }
    }, [filterNotices]);

    useEffect(() => {
        fetchNotices(true);
    }, [fetchNotices]);

    const loadMoreRef = useRef(null);
    useEffect(() => {
        if (!hasMore || loading || isLoadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    fetchNotices(false);
                }
            },
            { root: null, rootMargin: '100px', threshold: 0 }
        );

        const el = loadMoreRef.current;
        if (el) observer.observe(el);
        return () => (el ? observer.unobserve(el) : undefined);
    }, [hasMore, loading, isLoadingMore, fetchNotices]);

    const handleNoticeClick = notice => {
        navigate(`/notices/notice-detail`, { state: { notice: notice } });
    };

    return (
        <ContentArea>
            {/* Notice List */}
            <List
                sx={{
                    display: 'flex',
                    padding: '0px 10px',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flex: 1,
                    alignSelf: 'stretch',
                }}
            >
                {loading && notices.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : notices.length > 0 ? (
                    <>
                    {notices.map((notice, index) => (
                        <ListItem
                            key={notice.id}
                            button
                            onClick={() => handleNoticeClick(notice)}
                            sx={{
                                display: 'flex',
                                minWidth: 0,
                                padding: '20px 0px',
                                alignItems: 'center',
                                gap: '5px',
                                alignSelf: 'stretch',
                                borderBottom:
                                    index === notices.length - 1
                                        ? 'none'
                                        : '1px solid ' + theme.palette.figma?.white?.[30] || 'rgba(255, 255, 255, 0.3)',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    //   backgroundColor: notice.isNew ? '#FF43AA' : 'transparent',
                                    backgroundColor: 'transparent',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    flex: 1,
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }}
                            >
                                <ListItemText
                                    primary={notice.title}
                                    sx={{
                                        m: 0,
                                        width: '100%',
                                        maxWidth: '100%',
                                        '& .MuiListItemText-primary': {
                                            fontWeight: 500,
                                            fontSize: {
                                                xs: '0.95rem',
                                                sm: '1rem',
                                            },
                                            color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '100%',
                                        },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.text?.secondary || 'rgba(255, 255, 255, 0.4)',
                                        fontSize: '0.875rem',
                                        mt: 0.5,
                                        ml: 0,
                                    }}
                                >
                                    {formatDateTimeDisplay(
                                        notice.startDate || notice.createDate,
                                        'datetime'
                                    )}
                                </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ color: theme.palette.figma?.white?.[60] || theme.palette.figma?.icon || 'rgba(255, 255, 255, 0.6)' }} />
                        </ListItem>
                    ))}
                    {hasMore && <Box ref={loadMoreRef} sx={{ minHeight: 24, display: 'flex', justifyContent: 'center', py: 2 }}>
                        {isLoadingMore && <CircularProgress size={24} />}
                    </Box>}
                    </>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            height: '200px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.figma?.white?.[30] || 'rgba(255, 255, 255, 0.3)',
                                fontSize: '15px',
                                fontWeight: '500',
                                textAlign: 'center',
                            }}
                        >
                            {t('no_notices_registered')}
                        </Typography>
                    </Box>
                )}
            </List>
        </ContentArea>
    );
};

export default NoticeList;
